import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  HiOutlineUsers, 
  HiOutlineArchive, 
  HiOutlineTrash,
  HiOutlineSearch, 
  HiOutlineMail, 
  HiOutlineIdentification, 
  HiOutlineCalendar,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineXCircle
} from 'react-icons/hi';
import { 
  collection, 
  getDocs, 
  deleteDoc, 
  doc, 
  setDoc
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { sendEmployeeTerminationNotification } from '../../../emailjs/emailService';
import { fetchEmployees } from '../../../store/slices/authSlice';
import './TerminateEmployees.scss';

const TerminateEmployees = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('terminate');
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [terminatedEmployees, setTerminatedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveEmployeesModal, setShowActiveEmployeesModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [terminationReason, setTerminationReason] = useState('');
  const [terminating, setTerminating] = useState(false);
  const [auditSyncingEmployeeId, setAuditSyncingEmployeeId] = useState(null);
  const [authDeleteStatus, setAuthDeleteStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const OLD_DOMAIN = 'myelinh.com';

  const buildAuthDeleteCandidates = (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) return [];
    return [normalized];
  };

  // Get employees from Redux store
  const { employees } = useSelector(state => state.auth);

  useEffect(() => {
    refreshEmployeesData();
  }, []);

  // Watch for changes in Redux employees data
  useEffect(() => {
    if (employees && employees.length > 0) {
      setActiveEmployees(employees);
    }
  }, [employees]);

  const refreshEmployeesData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      
      // Fetch active employees from Redux store
      setActiveEmployees(employees || []);
      
      // Fetch terminated employees from archivedUsers collection
      const archivedUsersRef = collection(db, 'archivedUsers');
      const archivedSnapshot = await getDocs(archivedUsersRef);
      
      const terminatedData = archivedSnapshot.docs.map(doc => ({
        firestoreDocId: doc.id,
        ...doc.data()
      }));
      
      setTerminatedEmployees(terminatedData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setErrorMessage('Failed to fetch employee data');
    } finally {
      setLoading(false);
    }
  };

  const summarizeAuthAudit = (audit) => {
    if (!audit) return 'Auth deletion audit not recorded';
    const projectSuffix = audit.firebaseProjectId ? ` (project: ${audit.firebaseProjectId})` : '';
    if (audit.status === 'deleted') {
      const deletedCount = Number(audit.deletedCount || 0);
      const attemptedCount = Number(audit.attemptedCount || 0);
      return `Auth: deleted ${deletedCount}/${attemptedCount} selected account(s)${projectSuffix}`;
    }
    if (audit.status === 'none-found') {
      const attemptedCount = Number(audit.attemptedCount || 0);
      return `Auth: no matching account found for ${attemptedCount} selected email(s)${projectSuffix}`;
    }
    if (audit.status === 'failed') {
      return `Auth: deletion failed (${audit.error || 'unknown error'})${projectSuffix}`;
    }
    return 'Auth deletion audit unavailable';
  };

  const syncAuthDeletionAuditForArchivedEmployee = async (employee) => {
    if (!employee?.email) {
      setErrorMessage('Cannot run auth cleanup: employee email is missing');
      return;
    }

    const employeeDocId = employee.firestoreDocId || employee.email;
    try {
      setErrorMessage('');
      setAuditSyncingEmployeeId(employeeDocId);

      const authDeleteEmails = buildAuthDeleteCandidates(employee.email);
      const authDeleteResult = await deleteAuthUsersViaBackend(authDeleteEmails);
      const deleted = Number(authDeleteResult.deletedCount || 0);
      const authAudit = {
        status: deleted > 0 ? 'deleted' : 'none-found',
        firebaseProjectId: authDeleteResult.firebaseProjectId || null,
        deletedCount: deleted,
        attemptedCount: authDeleteEmails.length,
        results: authDeleteResult.results || [],
        updatedAt: new Date().toISOString(),
      };

      await setDoc(
        doc(db, 'archivedUsers', employeeDocId),
        { authDeletionAudit: authAudit },
        { merge: true }
      );

      const failedCount = Number(authDeleteResult.failedCount || 0);
      const projectNote = authDeleteResult.firebaseProjectId
        ? ` (project: ${authDeleteResult.firebaseProjectId})`
        : '';
      if (failedCount > 0) {
        setAuthDeleteStatus(
          `Auth cleanup for ${employee.email}: ${deleted} deleted, ${failedCount} failed${projectNote}. Check audit row for details.`
        );
      } else if (deleted > 0) {
        setAuthDeleteStatus(
          `Auth cleanup completed for ${employee.email} (${deleted} account(s) deleted)${projectNote}.`
        );
      } else {
        setAuthDeleteStatus(
          `Auth cleanup checked for ${employee.email}: no matching Firebase Auth account found${projectNote}.`
        );
      }
      await refreshEmployeesData();
    } catch (error) {
      console.error('Error running archived auth cleanup:', error);
      setErrorMessage(`Auth cleanup failed for ${employee.email}: ${error.message}`);
      try {
        await setDoc(
          doc(db, 'archivedUsers', employeeDocId),
          {
            authDeletionAudit: {
              status: 'failed',
              error: error.message,
              updatedAt: new Date().toISOString(),
            },
          },
          { merge: true }
        );
      } catch (writeError) {
        console.error('Failed to persist failed audit status:', writeError);
      }
    } finally {
      setAuditSyncingEmployeeId(null);
    }
  };

  const deleteAuthUsersViaBackend = async (emails) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const secret = process.env.REACT_APP_PPC_EMAIL_API_SECRET;
    if (!apiUrl || !secret) {
      throw new Error('Missing REACT_APP_API_URL or REACT_APP_PPC_EMAIL_API_SECRET');
    }

    const response = await fetch(`${apiUrl}/api/hr/ppc/users/delete-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-ppc-secret': secret,
      },
      body: JSON.stringify({ emails }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.error || `Auth deletion failed with status ${response.status}`);
    }
    return data;
  };

  const handleTerminateClick = (employee) => {
    setSelectedEmployee(employee);
    setShowTerminateModal(true);
    setErrorMessage('');
  };

  const handleTerminateConfirm = async () => {
    if (!selectedEmployee || !terminationReason.trim()) {
      setErrorMessage('Please provide a termination reason');
      return;
    }

    try {
      setTerminating(true);
      setErrorMessage('');

      // Prepare archived user data with termination metadata
      const archivedUserData = {
        ...selectedEmployee,
        terminatedAt: new Date(),
        terminationReason: terminationReason.trim(),
        terminatedBy: 'Admin', // You can get this from auth context
        originalId: selectedEmployee.id || selectedEmployee.empID
      };

      // Add to archivedUsers collection using email as document ID
      try {
        const archivedUsersRef = doc(db, 'archivedUsers', selectedEmployee.email);
        await setDoc(archivedUsersRef, archivedUserData);
        console.log('✅ Employee archived successfully:', selectedEmployee.email);
      } catch (archiveError) {
        console.error('❌ Error archiving employee:', archiveError);
        throw new Error('Failed to archive employee data');
      }

      // Delete from myeliners collection
      try {
        const myelinersRef = doc(db, 'myeliners', selectedEmployee.email);
        await deleteDoc(myelinersRef);
        console.log('✅ Employee deleted from myeliners collection:', selectedEmployee.email);
      } catch (deleteError) {
        console.error('❌ Error deleting from myeliners collection:', deleteError);
        // Don't fail the entire process if document deletion fails
        // The employee might already be deleted or the document might not exist
      }

      try {
        const authDeleteEmails = buildAuthDeleteCandidates(selectedEmployee.email);
        const authDeleteResult = await deleteAuthUsersViaBackend(authDeleteEmails);
        const deleted = Number(authDeleteResult.deletedCount || 0);
        const authAudit = {
          status: deleted > 0 ? 'deleted' : 'none-found',
          deletedCount: deleted,
          attemptedCount: authDeleteEmails.length,
          results: authDeleteResult.results || [],
          updatedAt: new Date().toISOString(),
        };
        await setDoc(
          doc(db, 'archivedUsers', selectedEmployee.email),
          { authDeletionAudit: authAudit },
          { merge: true }
        );
        setAuthDeleteStatus(
          deleted > 0
            ? `Firebase Auth cleanup completed (${deleted} account(s) deleted).`
            : 'No Firebase Auth account matched this employee email.'
        );
      } catch (authError) {
        console.error('Error deleting Firebase Auth user(s):', authError);
        await setDoc(
          doc(db, 'archivedUsers', selectedEmployee.email),
          {
            authDeletionAudit: {
              status: 'failed',
              error: authError.message,
              updatedAt: new Date().toISOString(),
            },
          },
          { merge: true }
        );
        setAuthDeleteStatus(
          `Employee archived, but Firebase Auth deletion failed: ${authError.message}`
        );
      }

      // Send termination notification email to HR team
      try {
        const emailResult = await sendEmployeeTerminationNotification(
          selectedEmployee,
          terminationReason.trim(),
          'Admin' // You can get this from auth context
        );
        
        if (emailResult.success) {
          console.log('✅ Termination notification email sent successfully');
        } else {
          console.error('❌ Failed to send termination notification email:', emailResult.error);
        }
      } catch (emailError) {
        console.error('❌ Error sending termination notification email:', emailError);
        // Don't fail the termination process if email fails
      }

      // Employee has been moved from myeliners to archivedUsers collection
      // Refresh the active employees list in Redux store
      await dispatch(fetchEmployees());
      
      // Close modal and refresh data
      setShowTerminateModal(false);
      setSelectedEmployee(null);
      setTerminationReason('');
      
      // Refresh the terminated employees list
      await refreshEmployeesData();
      
      console.log('Employee terminated successfully:', selectedEmployee.name);
    } catch (error) {
      console.error('Error terminating employee:', error);
      setErrorMessage('Failed to terminate employee');
    } finally {
      setTerminating(false);
    }
  };

  const handleTerminateCancel = () => {
    setShowTerminateModal(false);
    setSelectedEmployee(null);
    setTerminationReason('');
    setErrorMessage('');
    setAuthDeleteStatus('');
  };

  const handleMigrationCleanupConfirm = async () => {
    if (!selectedEmployee?.email) {
      setErrorMessage('Unable to process this record without an email');
      return;
    }

    try {
      setTerminating(true);
      setErrorMessage('');
      setAuthDeleteStatus('');

      const archivedUserData = {
        ...selectedEmployee,
        archived: new Date(),
        archivedBy: 'Admin',
        archiveType: 'migration-cleanup',
        archiveRemark: 'Old myelinh account removed after domain migration',
        originalId: selectedEmployee.id || selectedEmployee.empID
      };

      const archivedUsersRef = doc(db, 'archivedUsers', selectedEmployee.email);
      await setDoc(archivedUsersRef, archivedUserData);

      const myelinersRef = doc(db, 'myeliners', selectedEmployee.email);
      await deleteDoc(myelinersRef);

      try {
        const authDeleteEmails = buildAuthDeleteCandidates(selectedEmployee.email);
        const authDeleteResult = await deleteAuthUsersViaBackend(authDeleteEmails);
        const deleted = Number(authDeleteResult.deletedCount || 0);
        const authAudit = {
          status: deleted > 0 ? 'deleted' : 'none-found',
          deletedCount: deleted,
          attemptedCount: authDeleteEmails.length,
          results: authDeleteResult.results || [],
          updatedAt: new Date().toISOString(),
        };
        await setDoc(
          doc(db, 'archivedUsers', selectedEmployee.email),
          { authDeletionAudit: authAudit },
          { merge: true }
        );
        setAuthDeleteStatus(
          deleted > 0
            ? `Firebase Auth cleanup completed (${deleted} account(s) deleted).`
            : 'No Firebase Auth account matched this migrated user email.'
        );
      } catch (authError) {
        console.error('Error deleting Firebase Auth user(s):', authError);
        await setDoc(
          doc(db, 'archivedUsers', selectedEmployee.email),
          {
            authDeletionAudit: {
              status: 'failed',
              error: authError.message,
              updatedAt: new Date().toISOString(),
            },
          },
          { merge: true }
        );
        setAuthDeleteStatus(
          `Firestore cleanup succeeded, but Firebase Auth deletion failed: ${authError.message}`
        );
      }

      await dispatch(fetchEmployees());
      setShowTerminateModal(false);
      setSelectedEmployee(null);
      setTerminationReason('');
      await refreshEmployeesData();
    } catch (error) {
      console.error('Error cleaning up migrated employee account:', error);
      setErrorMessage('Failed to clean up old migrated account');
    } finally {
      setTerminating(false);
    }
  };

  // Filter employees based on search term
  const filterEmployees = (employeeList) => {
    if (!searchTerm) return employeeList;
    
    const query = searchTerm.toLowerCase();
    return employeeList.filter(emp => 
      emp.name?.toLowerCase().includes(query) ||
      emp.email?.toLowerCase().includes(query) ||
      emp.empID?.toLowerCase().includes(query) ||
      emp.department?.toLowerCase().includes(query)
    );
  };

  const oldDomainEmployees = activeEmployees.filter(
    (emp) => typeof emp.email === 'string' && emp.email.toLowerCase().endsWith(`@${OLD_DOMAIN}`)
  );

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  const renderActiveEmployeesModal = () => {
    const sourceEmployees = activeTab === 'terminate' ? activeEmployees : oldDomainEmployees;
    const filteredEmployees = filterEmployees(sourceEmployees);

    return (
      <div className="modal-overlay">
        <div className="modal-content large">
          <div className="modal-header">
            <h3>
              {activeTab === 'terminate'
                ? 'Select Employee to Terminate'
                : `Select Old ${OLD_DOMAIN} Account to Archive`}
            </h3>
            <button 
              className="modal-close"
              onClick={() => setShowActiveEmployeesModal(false)}
            >
              <HiOutlineX />
            </button>
          </div>
          
          <div className="modal-body">
            <div className="search-box">
              <HiOutlineSearch className="search-icon" />
              <input
                type="text"
                placeholder={
                  activeTab === 'terminate'
                    ? 'Search active employees...'
                    : `Search old ${OLD_DOMAIN} accounts...`
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading active employees...</p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="no-data">
                <HiOutlineUsers className="icon" />
                <h3>
                  {activeTab === 'terminate'
                    ? 'No Active Employees Found'
                    : `No ${OLD_DOMAIN} Accounts Found`}
                </h3>
                <p>
                  {searchTerm
                    ? 'Try adjusting your search criteria'
                    : activeTab === 'terminate'
                      ? 'No employees are currently active'
                      : `No active accounts found with @${OLD_DOMAIN} email`}
                </p>
              </div>
            ) : (
              <div className="active-employees-table">
                <div className="table-header">
                  <div className="table-cell name-col">Name</div>
                  <div className="table-cell email-col">Email</div>
                  <div className="table-cell empid-col">Employee ID</div>
                  <div className="table-cell dept-col">Department</div>
                  <div className="table-cell role-col">Role</div>
                  <div className="table-cell actions-col">Actions</div>
                </div>
                
                <div className="table-body">
                  {filteredEmployees.map(employee => (
                    <div key={`active-${employee.id || employee.empID}`} className="table-row active-row">
                      <div className="table-cell name-col">
                        <div className="employee-info">
                          {employee.img ? (
                            <img src={employee.img} alt={employee.name} className="employee-avatar" />
                          ) : (
                            <div className="employee-avatar placeholder">
                              {employee.name ? employee.name.charAt(0).toUpperCase() : 'E'}
                            </div>
                          )}
                          <div>
                            <h4>{employee.name || 'Unknown'}</h4>
                            <p>{employee.displayName || ''}</p>
                          </div>
                        </div>
                      </div>
                      <div className="table-cell email-col">
                        <div className="contact-info">
                          <HiOutlineMail className="icon" />
                          <span>{employee.email || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="table-cell empid-col">
                        <div className="contact-info">
                          <HiOutlineIdentification className="icon" />
                          <span>{employee.empID || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="table-cell dept-col">{employee.department || 'N/A'}</div>
                      <div className="table-cell role-col">{employee.role || 'N/A'}</div>
                      <div className="table-cell actions-col">
                        <button
                          className={activeTab === 'terminate' ? 'btn-terminate' : 'btn-cleanup'}
                          onClick={() => {
                            setSelectedEmployee(employee);
                            setShowActiveEmployeesModal(false);
                            setShowTerminateModal(true);
                            setSearchTerm('');
                            setAuthDeleteStatus('');
                          }}
                          title={activeTab === 'terminate' ? 'Terminate Employee' : 'Archive old account'}
                        >
                          {activeTab === 'terminate' ? (
                            <>
                              <HiOutlineX className="icon" />
                              Terminate
                            </>
                          ) : (
                            <>
                              <HiOutlineTrash className="icon" />
                              Archive Old Account
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTerminatedEmployeesTable = () => {
    const filteredEmployees = filterEmployees(terminatedEmployees);

    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading terminated employees...</p>
        </div>
      );
    }

    if (filteredEmployees.length === 0) {
      return (
        <div className="no-data">
          <HiOutlineArchive className="icon" />
          <h3>No Terminated Employees Found</h3>
          <p>{searchTerm ? 'Try adjusting your search criteria' : 'No employees have been terminated'}</p>
        </div>
      );
    }

    return (
      <div className="terminated-employees-table">
        <div className="table-header">
          <div className="table-cell name-col">Name</div>
          <div className="table-cell email-col">Email</div>
          <div className="table-cell empid-col">Employee ID</div>
          <div className="table-cell dept-col">Department</div>
          <div className="table-cell reason-col">Termination Reason</div>
          <div className="table-cell date-col">Terminated Date</div>
        </div>
        
        <div className="table-body">
          {filteredEmployees.map((employee, index) => {
            const rowKey = `terminated-${employee.firestoreDocId || employee.email || employee.empID || index}`;
            return (
            <React.Fragment key={`${rowKey}-${index}`}>
              <div className="table-row terminated-row">
                <div className="table-cell name-col">
                  <div className="employee-info">
                    {employee.img ? (
                      <img src={employee.img} alt={employee.name} className="employee-avatar" />
                    ) : (
                      <div className="employee-avatar placeholder">
                        {employee.name ? employee.name.charAt(0).toUpperCase() : 'E'}
                      </div>
                    )}
                    <div>
                      <h4>{employee.name || 'Unknown'}</h4>
                      <p>{employee.displayName || ''}</p>
                    </div>
                  </div>
                </div>
                <div className="table-cell email-col">
                  <div className="contact-info">
                    <HiOutlineMail className="icon" />
                    <span>{employee.email || 'N/A'}</span>
                  </div>
                </div>
                <div className="table-cell empid-col">
                  <div className="contact-info">
                    <HiOutlineIdentification className="icon" />
                    <span>{employee.empID || 'N/A'}</span>
                  </div>
                </div>
                <div className="table-cell dept-col">{employee.department || 'N/A'}</div>
                <div className="table-cell reason-col">
                  <span className="termination-reason">{employee.terminationReason || 'N/A'}</span>
                </div>
                <div className="table-cell date-col">
                  <div className="contact-info">
                    <HiOutlineCalendar className="icon" />
                    <span>{formatDate(employee.terminatedAt)}</span>
                  </div>
                </div>
              </div>
              <div className="table-row terminated-row auth-audit-row">
                <div className="table-cell" style={{ gridColumn: '1 / -1' }}>
                  <strong>Auth deletion:</strong> {summarizeAuthAudit(employee.authDeletionAudit)}
                  <button
                    type="button"
                    className="btn-cleanup"
                    style={{ marginLeft: '12px' }}
                    onClick={() => syncAuthDeletionAuditForArchivedEmployee(employee)}
                    disabled={auditSyncingEmployeeId === (employee.firestoreDocId || employee.email)}
                  >
                    {auditSyncingEmployeeId === (employee.firestoreDocId || employee.email)
                      ? 'Checking...'
                      : 'Run Auth Cleanup'}
                  </button>
                </div>
              </div>
            </React.Fragment>
          );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="terminate-employees-container">
      <div className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'terminate' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('terminate');
              setSearchTerm('');
              setErrorMessage('');
            }}
          >
            Terminated Employees
          </button>
          <button
            className={`tab-button ${activeTab === 'cleanup' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('cleanup');
              setSearchTerm('');
              setErrorMessage('');
            }}
          >
            Migration Cleanup (Old {OLD_DOMAIN} Accounts)
          </button>
        </div>
      </div>

      <div className="page-header">
        <div className="header-content">
          <h1>{activeTab === 'terminate' ? 'Terminated Employees' : 'Migration Cleanup'}</h1>
          <p>
            {activeTab === 'terminate'
              ? 'View and manage terminated employees'
              : `Archive and remove old @${OLD_DOMAIN} employee entities after domain migration`}
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn-primary"
            onClick={() => setShowActiveEmployeesModal(true)}
          >
            {activeTab === 'terminate' ? (
              <>
                <HiOutlineUsers className="icon" />
                Terminate Another Employee
              </>
            ) : (
              <>
                <HiOutlineTrash className="icon" />
                Archive Old Account
              </>
            )}
          </button>
        </div>
      </div>

      <div className="search-section">
        <div className="search-box">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder={
              activeTab === 'terminate'
                ? 'Search terminated employees by name, email, ID, or department...'
                : `Search old @${OLD_DOMAIN} active accounts by name, email, ID, or department...`
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {errorMessage && (
        <div className="error-message">
          <HiOutlineXCircle className="icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="terminated-employees-section">
        {activeTab === 'terminate' ? renderTerminatedEmployeesTable() : (
          <div className="terminated-employees-table">
            <div className="table-header">
              <div className="table-cell name-col">Name</div>
              <div className="table-cell email-col">Email</div>
              <div className="table-cell empid-col">Employee ID</div>
              <div className="table-cell dept-col">Department</div>
              <div className="table-cell reason-col">Action</div>
              <div className="table-cell date-col">Status</div>
            </div>
            <div className="table-body">
              {filterEmployees(oldDomainEmployees).map(employee => (
                <div key={`cleanup-${employee.id || employee.empID}`} className="table-row active-row">
                  <div className="table-cell name-col">
                    <div className="employee-info">
                      {employee.img ? (
                        <img src={employee.img} alt={employee.name} className="employee-avatar" />
                      ) : (
                        <div className="employee-avatar placeholder">
                          {employee.name ? employee.name.charAt(0).toUpperCase() : 'E'}
                        </div>
                      )}
                      <div>
                        <h4>{employee.name || 'Unknown'}</h4>
                        <p>{employee.displayName || ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="table-cell email-col">
                    <div className="contact-info">
                      <HiOutlineMail className="icon" />
                      <span>{employee.email || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="table-cell empid-col">
                    <div className="contact-info">
                      <HiOutlineIdentification className="icon" />
                      <span>{employee.empID || 'N/A'}</span>
                    </div>
                  </div>
                  <div className="table-cell dept-col">{employee.department || 'N/A'}</div>
                  <div className="table-cell reason-col">
                    <span className="termination-reason">Archive old account</span>
                  </div>
                  <div className="table-cell date-col">
                    <button
                      className="btn-cleanup"
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setShowTerminateModal(true);
                        setAuthDeleteStatus('');
                      }}
                    >
                      <HiOutlineTrash className="icon" />
                      Archive
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {filterEmployees(oldDomainEmployees).length === 0 && (
              <div className="no-data">
                <HiOutlineArchive className="icon" />
                <h3>No old {OLD_DOMAIN} accounts found</h3>
                <p>All active entities are already on the new domain or do not match your search.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active Employees Selection Modal */}
      {showActiveEmployeesModal && renderActiveEmployeesModal()}

      {/* Termination Confirmation Modal */}
      {showTerminateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {activeTab === 'terminate'
                  ? 'Confirm Employee Termination'
                  : 'Confirm Old Account Archival'}
              </h3>
              <button 
                className="modal-close"
                onClick={handleTerminateCancel}
              >
                <HiOutlineX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="employee-details">
                <div className="employee-info">
                  {selectedEmployee?.img ? (
                    <img src={selectedEmployee.img} alt={selectedEmployee.name} className="employee-avatar" />
                  ) : (
                    <div className="employee-avatar placeholder">
                      {selectedEmployee?.name ? selectedEmployee.name.charAt(0).toUpperCase() : 'E'}
                    </div>
                  )}
                  <div>
                    <h4>{selectedEmployee?.name || 'Unknown'}</h4>
                    <p>{selectedEmployee?.email || 'N/A'}</p>
                    <p>ID: {selectedEmployee?.empID || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {activeTab === 'terminate' ? (
                <div className="form-group">
                  <label htmlFor="terminationReason">Termination Reason *</label>
                  <textarea
                    id="terminationReason"
                    value={terminationReason}
                    onChange={(e) => setTerminationReason(e.target.value)}
                    placeholder="Please provide a reason for termination..."
                    rows={4}
                    required
                  />
                </div>
              ) : (
                <div className="cleanup-note">
                  This action is for migration cleanup only. It archives this old account, removes it from
                  active employees, and attempts to delete the Firebase Auth user for this email.
                </div>
              )}
              {authDeleteStatus && (
                <div className="cleanup-auth-status">{authDeleteStatus}</div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={handleTerminateCancel}
                disabled={terminating}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={activeTab === 'terminate' ? handleTerminateConfirm : handleMigrationCleanupConfirm}
                disabled={terminating || (activeTab === 'terminate' && !terminationReason.trim())}
              >
                {terminating ? (
                  <>
                    <div className="loading-spinner small"></div>
                    {activeTab === 'terminate' ? 'Terminating...' : 'Archiving...'}
                  </>
                ) : (
                  <>
                    {activeTab === 'terminate' ? (
                      <>
                        <HiOutlineCheck className="icon" />
                        Confirm Termination
                      </>
                    ) : (
                      <>
                        <HiOutlineTrash className="icon" />
                        Confirm Archive
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerminateEmployees;
